/* eslint-disable max-lines */
import React, { useEffect, useMemo, useState } from 'react';
import Header from '../../components/Header';
import { useDispatch, useSelector } from 'react-redux';
import { useAsync } from 'react-use';
import { Wrapper, Title, Buttons } from '../../styles';
import Pdf from 'components/Pdf/newPdf';
import utils from 'utils';
import services from 'services';
import selectors from 'state/selectors';
import { useErrors, errorColums } from './useErrors';

import { UserCompany, Error } from 'models/response';
import Table from 'components/Table';
import CommonButton from 'components/Buttons/CommonButton';
import actions from 'state/actions';
import styled from 'styled-components';
import { modalActionType } from 'utils/constants';
import Spinner from 'components/Spinner/Spinner';

const FlexContainer = styled.div`
  display: flex;
  align-items: center;
`;

export interface CompanyOption {
  label: string;
  value: string;
}

const Errors = () => {
  const t = utils.intl.useTranslator();
  const companies: UserCompany[] = useSelector(selectors.app.getCompanies);
  const dispatch = useDispatch();
  const [selectedCompany, setSelectedCompany] = useState<{ label: string; value: string } | null>(null);
  const [selectedErrors, setSelectedErrors] = useState<number[]>([]);
  const [pdfBase64, setPdfBase64] = useState<string | null>(null);

  const userPreference = useSelector(selectors.user.getUserPreference);
  const isModalVisible = useSelector(selectors.modal.getVisibility);

  // Use SWR hook for caching
  const { data: errors, isLoading, refresh } = useErrors(selectedCompany ? Number(selectedCompany.value) : null);

  const columns = useMemo(() => errorColums(t, userPreference), [t, userPreference]);

  const companyOptions = useMemo(() => {
    const values = utils.input.removeDefaultValue(companies, 'idCompany');
    const options = utils.input.buildOptions(values, 'name', 'idCompany');

    return options;
  }, [companies]);

  const onSelection = (selection: Error[]) => {
    setSelectedErrors(selection.map(({ protocol }) => protocol));
  };

  useAsync(async () => {
    try {
      if (selectedErrors.length === 1) {
        const { data } = await services.getMonitorDocumentImage(selectedErrors[0]);
        setPdfBase64(data);
      } else {
        setPdfBase64(null);
      }
    } catch (err) {
      console.error(err);
      setPdfBase64(null);
    }
  }, [selectedErrors]);

  useEffect(() => {
    setPdfBase64(null);
    setSelectedErrors([]);
  }, [isModalVisible, selectedCompany]);

  const deleteErrors = async () => {
    try {
      await services.deleteErrors(selectedErrors);
      refresh();
      setSelectedErrors([]);
      utils.app.notify('success', t('deleted'));
    } catch (e) {
      utils.app.notify('fail', t('not_deleted'));
      console.error(e);
    }
  };

  const upperRowSection = () => {
    if (!selectedCompany) return null;
    if (isLoading) return <Spinner />;

    return errors.length ? (
      <>
        <Table
          id="errors-table"
          fixedColumns={3}
          rowId="protocolIn"
          hasToolbar
          columns={columns}
          rows={errors}
          onSelection={onSelection}
          hasMultiSelection
          hasPagination
          hasResize
          hasSort
          hasFilter
        />
      </>
    ) : (
      <div>{t('no_data')}</div>
    );
  };
  const retryErrors = async (props: { selectedErrors: number[]; selectedCompany?: string; refresh: () => void }) => {
    dispatch(
      actions.modal.setModal({
        actionType: modalActionType.normalizer.LOADING_ERROR,
        props,
      }),
    );
    setSelectedErrors([]);
  };

  const bottomRowSection = () =>
    pdfBase64 && !isModalVisible ? <Pdf base64={pdfBase64} height="70vh" key="right-col" /> : null;

  const downloadError = async () => {
    try {
      const { data } = await services.downloadError(selectedErrors);
      if (data?.size === 0) {
        utils.app.notify('warning', t('size-0-zip'));
        return;
      }
      utils.file.downloadFileZip(data, 'errors-pdf.zip');
    } catch (e) {
      console.error(e);
    }
  };

  return companies ? (
    <>
      <Header
        companies={companyOptions}
        companySelection={true}
        setSelectedCompany={setSelectedCompany}
        selectedCompany={selectedCompany}
      >
        <FlexContainer>
          <CommonButton
            id="errors-download-btn"
            disabled={selectedErrors.length === 0}
            customIcon="file-download"
            scope="tertiary"
            value={t('download-error')}
            action={() => downloadError()}
          />
        </FlexContainer>
      </Header>
      <Wrapper>
        <Title>
          <h2>{t('errors_title')}</h2>
        </Title>
        <Buttons>
          <CommonButton
            id="errors-retry-all-btn"
            scope="secondary"
            value={t('retry-all')}
            type="button"
            action={() =>
              dispatch(
                actions.modal.setModal({
                  actionType: 'normalizer/retryErrors',
                  props: {
                    errorNumber: errors.length,
                    func: () =>
                      retryErrors({
                        selectedErrors: errors.map(({ protocol }) => protocol),
                        selectedCompany: selectedCompany?.value,
                        refresh,
                      }),
                  },
                }),
              )
            }
            disabled={errors.length === 0 || utils.user.isActionByCompanyActive('retryAllErrors') === false}
          />
          <CommonButton
            id="errors-retry-btn"
            scope="secondary"
            value={t('retry')}
            type="button"
            action={() =>
              retryErrors({
                selectedErrors,
                selectedCompany: selectedCompany?.value,
                refresh,
              })
            }
            disabled={!selectedErrors.length}
          />
          <CommonButton
            id="errors-delete-btn"
            scope="secondary"
            value={t('delete')}
            type="button"
            action={() => deleteErrors()}
            disabled={!selectedErrors.length}
          />
        </Buttons>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {upperRowSection()}
          {bottomRowSection()}
        </div>
      </Wrapper>
    </>
  ) : null;
};

export default Errors;
